import { useEffect, useRef, useState, type ReactNode } from "react";
import { setBearerToken, getBearerToken } from "@/lib/auth-store";
import { LoginScreen } from "./LoginScreen";
import { setBookingGridMinutes, setDisplayTimeZone } from "@/lib/format";

/**
 * Unified auth bootstrap for SyncSuite-embedded projects.
 *
 * The static-gateway has already consumed any `?__syncsuite_token=` URL
 * token at the edge and converted it into an HttpOnly `__syncsuite_session`
 * cookie (Partitioned, so it works in iframes AND new tabs alike). By the
 * time this component mounts, either:
 *
 *   a) We already have a bearer token in memory (HMR / re-render) → done.
 *   b) The gateway cookie is set → POST /api/syncsuite-login, backend
 *      verifies the cookie against mothership, signs in via BetterAuth,
 *      returns a bearer token we stash in memory.
 *   c) No cookie (app opened without a token, e.g. direct URL visit to
 *      a live project) → if we're the top-level page, reload ONCE with
 *      `?__syncsuite_sso=1` — SyncSuite's edge sees the marker and
 *      bounces through the dashboard's single sign-on, which sets the
 *      cookie and strips the URL clean (SSO "pull" flow). If that
 *      round-trip still can't sign us in (not logged in to SyncSuite /
 *      no access to this workspace), fall through to:
 *   d) A friendly error with instructions.
 *
 * No postMessage, no URL-token stripping, no iframe-vs-newtab branching —
 * the gateway normalizes everything into a cookie before we ever run.
 */

// One-shot guard so the SSO reload can never loop: set before we redirect,
// cleared on a successful login so a later expiry can self-heal again.
const SSO_TRIED_KEY = "__syncsuite_sso_tried";

function trySsoRedirect(): boolean {
  // Only when we own the whole tab — inside the dashboard's iframe the
  // parent workspace drives auth, and hijacking the top window from an
  // embedded frame would navigate the customer's dashboard away.
  if (window.self !== window.top) return false;
  try {
    if (sessionStorage.getItem(SSO_TRIED_KEY)) return false;
    sessionStorage.setItem(SSO_TRIED_KEY, "1");
  } catch {
    return false; // storage unavailable → never risk a reload loop
  }
  const u = new URL(window.location.href);
  u.searchParams.set("__syncsuite_sso", "1");
  window.location.replace(u.toString());
  return true;
}
export function AuthGate({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(() => !!getBearerToken());
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  // Prevents a double-mount (React StrictMode, HMR) from firing the
  // login fetch twice and racing against itself.
  const loginStarted = useRef(false);

  // How this practice signs in. "password" = its own login screen, which is
  // what a med spa needs: most of her team will never see SyncSuite. Set
  // VITE_LOGIN_MODE=syncsuite to go back to single sign-on only.
  const loginMode = (import.meta.env.VITE_LOGIN_MODE as string | undefined) ?? "password";

  // Every time in the app renders in the practice's timezone, not the
  // browser's. Fetched before auth because the login screen shows dates too.
  useEffect(() => {
    fetch("/api/public/branding")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.branding?.timezone) setDisplayTimeZone(d.branding.timezone);
        setBookingGridMinutes(d?.branding?.slot_step_minutes);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (getBearerToken()) { setAuthenticated(true); return; }
    if (loginStarted.current) return;
    loginStarted.current = true;

    // DEV BYPASS: When VITE_DEV_AUTH_BYPASS=1, skip the SyncSuite handshake
    // and stamp a fake bearer token. The backend honors a matching env var
    // (CRM_DEV_AUTH_BYPASS=1) so the rest of the API works without a real
    // BetterAuth session. Both sides MUST be enabled for this to work, and
    // both sides refuse to enable in production. Remove (or unset the env
    // vars) before deploying.
    // Password mode: don't reach for SyncSuite at all — but do try the
    // gateway cookie ONCE, so Matthew opening it from the dashboard still
    // lands signed in rather than at a login form he doesn't need.
    if (loginMode === "password") {
      // Ask "am I already signed in?" first. A returning user almost always
      // is — their session cookie is still good — and asking SyncSuite first
      // would fire a pointless 401 into the console on every page load.
      fetch("/api/staff/me", { credentials: "include" })
        .then((r) => {
          if (r.ok) { setAuthenticated(true); return; }
          throw new Error("no session");
        })
        .catch(() => trySyncsuiteThenLogin());
      return;
    }

    function trySyncsuiteThenLogin() {
      fetch("/api/syncsuite-login", {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({}),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error("no sso");
          const data = (await res.json()) as { bearerToken?: string };
          if (!data.bearerToken) throw new Error("no token");
          setBearerToken(data.bearerToken);
          setAuthenticated(true);
        })
        .catch(() => setNeedsLogin(true));
    }

    // A SyncSuite TEST copy runs Vite in development mode while reading the
    // account's LIVE data, so MODE alone would let the bypass skip the
    // handshake there. The page must also be served from localhost, which a
    // SyncSuite copy never is.
    const onLocalhost = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
    if (import.meta.env.VITE_DEV_AUTH_BYPASS === "1" && import.meta.env.MODE !== "production" && onLocalhost) {
      // eslint-disable-next-line no-console
      console.warn("[AuthGate] ⚠️  VITE_DEV_AUTH_BYPASS=1 — skipping login");
      setBearerToken("dev-bypass-token");
      setAuthenticated(true);
      return;
    }

    const login = () =>
      fetch("/api/syncsuite-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Empty body — the gateway's __syncsuite_session cookie IS the
        // credential. credentials:"include" ensures the cookie is sent
        // even when this file is served from a cross-origin CDN.
        credentials: "include",
        body: JSON.stringify({}),
      }).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error || `Login failed (${res.status})`);
        }
        const data = (await res.json()) as { bearerToken?: string };
        if (!data.bearerToken) throw new Error("No bearer token in response");
        setBearerToken(data.bearerToken);
        setAuthenticated(true);
        try { sessionStorage.removeItem(SSO_TRIED_KEY); } catch { /* ignore */ }
      });

    login()
      .catch(
        // One silent retry after 1.5s. The login leans on a mothership
        // verify round-trip that can transiently flake (timeout, backend
        // restart); historically that surfaced as "Invalid or expired
        // preview session" which a manual refresh fixed instantly. The
        // retry is that refresh, automated.
        () => new Promise((r) => setTimeout(r, 1500)).then(login)
      )
      .catch((err: Error) => {
        if (trySsoRedirect()) return; // navigating away; keep the spinner up
        console.error("[AuthGate] Login failed:", err);
        setError(err.message);
      });
  }, [loginMode]);

  // No SyncSuite session and the practice signs in with a password: show it.
  if (needsLogin && !authenticated) {
    return <LoginScreen onSignedIn={() => { setNeedsLogin(false); setAuthenticated(true); }} />;
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
        <p>Unable to sign in. Please open this app from your SyncSuite dashboard.</p>
        <p style={{ fontSize: 12, marginTop: 8, color: "#999" }}>{error}</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "#ffffff",
      }}>
        <div style={{
          width: 24,
          height: 24,
          border: "2px solid #e4e4e7",
          borderTopColor: "#6366f1",
          borderRadius: "50%",
          animation: "spin 0.6s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return <>{children}</>;
}
